<template>
    <div class="fixed inset-0 bg-black mt-10 bg-opacity-60 flex items-center justify-center z-50 p-2" @click="$emit('close')">
        <div 
            :class="[
                'bg-white dark:bg-gray-900 rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-all duration-300',
                isFullscreen 
                    ? 'fullscreen' 
                    : 'max-w-6xl w-full max-h-[85vh]'
            ]" 
            @click.stop
        >
            <!-- Header -->
            <div class="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <div>
                    <h3 class="text-xl font-bold text-gray-900 dark:text-white">Documentos Anexados</h3>
                    <p class="text-sm text-gray-600 dark:text-gray-400">
                        {{ appointment?.patient_name }} - {{ appointment?.service_name }}
                    </p>
                    <p v-if="appointment?.accession_number" class="text-xs text-blue-600 dark:text-blue-400 font-medium mt-1">
                        OS: {{ appointment.accession_number }}
                    </p>
                </div>
                <div class="flex items-center gap-2">
                    <button 
                        v-if="documents.length > 0"
                        @click="downloadAllDocuments"
                        class="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-semibold flex items-center gap-2 shadow transition"
                        :disabled="isDownloadingZip"
                        title="Download de todos os documentos em ZIP"
                    >
                        <ArchiveIcon v-if="!isDownloadingZip" class="w-4 h-4" />
                        <div v-else class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        {{ isDownloadingZip ? 'Preparando...' : 'Download ZIP' }}
                    </button>
                    <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-full p-2 transition">
                        <XIcon class="w-6 h-6" />
                    </button>
                </div>
            </div>

            <!-- Content -->
            <div class="flex-1 flex flex-col md:flex-row overflow-hidden">
                <!-- Document List -->
                <div class="md:w-1/3 w-full border-r border-gray-200 dark:border-gray-700 overflow-y-auto bg-gray-50 dark:bg-gray-800 p-4">
                    <!-- Loading State for Documents List -->
                    <div v-if="isLoading" class="text-center py-8">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p class="text-gray-600 dark:text-gray-400">Carregando documentos...</p>
                    </div>
                    
                    <div v-else-if="documents.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400">
                        <FileTextIcon class="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>Nenhum documento anexado</p>
                        <p class="text-sm mt-2">Os documentos aparecerão aqui após serem anexados</p>
                    </div>
                    
                    <div v-else class="space-y-3">
                        <div 
                            v-for="(document, index) in documents" 
                            :key="document.document_id"
                            @click="selectDocument(document)"
                            :class="[
                                'document-item p-3 rounded-xl border cursor-pointer transition-all flex gap-3 items-center',
                                selectedDocument?.document_id === document.document_id
                                    ? 'border-blue-500 bg-blue-100/60 dark:bg-blue-900/30 shadow-lg'
                                    : 'border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                            ]"
                        >
                            <img 
                                v-if="document.thumbnail_url" 
                                :src="document.thumbnail_url" 
                                :alt="`Thumbnail ${index + 1}`"
                                class="w-8 h-8 object-cover rounded border border-gray-200 dark:border-gray-600 bg-white"
                                style="width: 40px !important; height: 40px !important; max-width: 40px !important; max-height: 40px !important;"
                            />
                            <div v-else class="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center" style="width: 40px !important; height: 40px !important;">
                                <FileTextIcon class="w-4 h-4 text-gray-400" style="width: 20px !important; height: 20px !important;" />
                            </div>
                            <div class="flex-1 min-w-0">
                                <div class="flex items-center gap-2 mb-1">
                                    <span class="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wide">
                                        {{ document.document_type || 'Documento' }}
                                    </span>
                                    <span v-if="document.page_number && document.total_pages > 1" class="text-xs text-gray-400">pág. {{ document.page_number }}/{{ document.total_pages }}</span>
                                </div>
                                <p v-if="document.description" class="text-xs text-gray-700 dark:text-gray-300 truncate">
                                    {{ document.description }}
                                </p>
                                <p class="text-xs text-gray-500 dark:text-gray-400">
                                    {{ formatFileSize(document.size) }}
                                </p>
                                <p v-if="document.uploaded_by" class="text-xs text-green-600 dark:text-green-400 font-medium">
                                    Anexado por: {{ document.uploaded_by }}
                                </p>
                                <p v-if="document.upload_date" class="text-xs text-gray-400 dark:text-gray-500">
                                    {{ formatDate(document.upload_date) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Document Viewer -->
                <div class="flex-1 flex flex-col bg-white dark:bg-gray-900">
                    <!-- Viewer Header -->
                    <div v-if="selectedDocument" class="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center gap-4">
                                <h4 class="font-semibold text-gray-900 dark:text-white uppercase">
                                    {{ selectedDocument.document_type || 'Documento' }}
                                </h4>
                                <span v-if="selectedDocument.page_number && selectedDocument.total_pages > 1" class="text-sm text-gray-500">
                                    Página {{ selectedDocument.page_number }} de {{ selectedDocument.total_pages }}
                                </span>
                                <span class="text-sm text-gray-400">
                                    {{ currentDocumentIndex + 1 }} de {{ documents.length }}
                                </span>
                            </div>
                            <div class="flex items-center gap-2">
                                <button 
                                    @click="toggleFullscreen"
                                    class="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                                    :title="isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'"
                                >
                                    <MaximizeIcon v-if="!isFullscreen" class="w-5 h-5" />
                                    <MinimizeIcon v-else class="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Viewer Content -->
                    <div class="flex-1 flex items-center justify-center p-6 relative overflow-hidden viewer-container">
                        <!-- No Document Selected -->
                        <div v-if="!selectedDocument" class="text-center text-gray-500 dark:text-gray-400">
                            <EyeIcon class="w-16 h-16 mx-auto mb-4 opacity-50" />
                            <p class="text-lg">Selecione um documento para visualizar</p>
                            <p class="text-sm mt-2">Clique em um documento na lista ao lado</p>
                        </div>

                        <!-- Document Viewer -->
                        <div v-else class="w-full h-full flex flex-col items-center justify-center max-w-full max-h-full">
                            <!-- Loading State -->
                            <div v-if="isLoadingDocument" class="text-center">
                                <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                <p class="text-gray-600 dark:text-gray-400 text-lg">Carregando documento...</p>
                                <p class="text-sm text-gray-500 mt-2">Isso pode levar alguns segundos</p>
                            </div>

                            <!-- Error State -->
                            <div v-else-if="documentError" class="text-center max-w-md">
                                <div class="error-container rounded-xl p-6 border border-red-200 dark:border-red-800">
                                    <XIcon class="w-12 h-12 text-red-500 mx-auto mb-4" />
                                    <h3 class="text-lg font-semibold text-red-700 dark:text-red-400 mb-2">
                                        Erro ao carregar documento
                                    </h3>
                                    <p class="text-sm text-red-600 dark:text-red-300 mb-4">
                                        {{ documentError }}
                                    </p>
                                    <button 
                                        @click="loadDocument(selectedDocument)"
                                        class="px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition flex items-center gap-2 mx-auto btn-danger"
                                    >
                                        <RefreshCwIcon class="w-4 h-4" />
                                        Tentar Novamente
                                    </button>
                                </div>
                            </div>

                            <!-- Document Display -->
                            <div v-else-if="documentUrl" class="relative w-full h-full flex items-center justify-center max-w-full max-h-full">
                                <!-- Navigation Arrows -->
                                <button 
                                    v-if="canNavigatePrevious"
                                    @click="navigateDocument(-1)"
                                    class="navigation-arrow absolute left-4 top-1/2 transform -translate-y-1/2 z-10 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all duration-200"
                                    title="Documento anterior (←)"
                                >
                                    <ChevronLeftIcon class="w-6 h-6" />
                                </button>

                                <button 
                                    v-if="canNavigateNext"
                                    @click="navigateDocument(1)"
                                    class="navigation-arrow absolute right-4 top-1/2 transform -translate-y-1/2 z-10 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all duration-200"
                                    title="Próximo documento (→)"
                                >
                                    <ChevronRightIcon class="w-6 h-6" />
                                </button>

                                <!-- Document Image Container with Drag Support -->
                                <div 
                                    class="w-full h-full flex items-center justify-center p-4 overflow-hidden drag-container"
                                    @mousedown="startDrag"
                                    @mousemove="onDrag"
                                    @mouseup="stopDrag"
                                    @mouseleave="stopDrag"
                                    @touchstart="startTouch"
                                    @touchmove="onTouchMove"
                                    @touchend="stopDrag"
                                    @wheel="onWheel"
                                    :class="{ 'cursor-grab': !isDragging && currentZoom > 1, 'cursor-grabbing': isDragging }"
                                >
                                    <img 
                                        :src="documentUrl"
                                        :alt="`Documento ${selectedDocument.document_id}`"
                                        :style="{ 
                                            transform: `scale(${currentZoom}) translate(${dragOffset.x}px, ${dragOffset.y}px)`,
                                            maxWidth: 'calc(100% - 2rem)',
                                            maxHeight: 'calc(100% - 2rem)',
                                            width: 'auto',
                                            height: 'auto'
                                        }"
                                        class="object-contain rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 bg-white transition-transform duration-300 ease-in-out"
                                        @load="onImageLoad"
                                        @error="onImageError"
                                        draggable="false"
                                    />
                                </div>

                                <!-- Zoom Controls -->
                                <div class="absolute bottom-4 right-4 flex gap-2">
                                    <button 
                                        @click="zoomOut"
                                        class="zoom-control p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg transition"
                                        title="Diminuir zoom (-)"
                                    >
                                        <MinusIcon class="w-4 h-4" />
                                    </button>
                                    <button 
                                        @click="resetZoom"
                                        class="zoom-control px-3 py-2 bg-black/50 hover:bg-black/70 text-white rounded-lg transition text-sm font-medium"
                                        title="Zoom padrão (0)"
                                    >
                                        {{ Math.round(currentZoom * 100) }}%
                                    </button>
                                    <button 
                                        @click="zoomIn"
                                        class="zoom-control p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg transition"
                                        title="Aumentar zoom (+)"
                                    >
                                        <PlusIcon class="w-4 h-4" />
                                    </button>
                                </div>

                                <!-- Drag Instructions -->
                                <div v-if="currentZoom > 1" class="absolute top-4 left-4 text-xs text-white/70 bg-black/30 px-2 py-1 rounded">
                                    <span class="hidden md:inline">Arraste para mover • Scroll para zoom</span>
                                    <span class="md:hidden">Toque e arraste para mover</span>
                                </div>

                                <!-- Keyboard Shortcuts Info -->
                                <div class="absolute bottom-4 left-4 text-xs text-white/70 bg-black/30 px-2 py-1 rounded">
                                    <span class="hidden md:inline">← → navegar • Scroll zoom • + - zoom • 0 reset • ESC sair</span>
                                    <span class="md:hidden">Toque para navegar • Pinch para zoom</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Document Info Footer -->
                    <div v-if="selectedDocument" class="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                        <div class="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                            <div class="flex flex-col gap-1 flex-1">
                                <div class="flex items-center gap-4">
                                    <span class="text-sm font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide">
                                        {{ selectedDocument.document_type || 'Documento' }}
                                    </span>
                                    <span v-if="selectedDocument.page_number && selectedDocument.total_pages > 1" class="text-sm text-gray-500">
                                        Página {{ selectedDocument.page_number }} de {{ selectedDocument.total_pages }}
                                    </span>
                                </div>
                                <p v-if="selectedDocument.description" class="text-sm text-gray-700 dark:text-gray-300">
                                    {{ selectedDocument.description }}
                                </p>
                                <div class="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                                    <span>{{ formatFileSize(selectedDocument.size) }}</span>
                                    <span v-if="selectedDocument.uploaded_by">
                                        Anexado por: {{ selectedDocument.uploaded_by }}
                                    </span>
                                    <span v-if="selectedDocument.upload_date">
                                        {{ formatDate(selectedDocument.upload_date) }}
                                    </span>
                                </div>
                            </div>
                            <div class="flex gap-2">
                                
                                <button 
                                    v-if="canDeleteDocument"
                                    @click="deleteDocument(selectedDocument)"
                                    class="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-semibold flex items-center gap-2 shadow transition btn-danger"
                                >
                                    <TrashIcon class="w-4 h-4" />
                                    Excluir
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer -->
            <div class="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800">
                <div class="text-sm text-gray-600 dark:text-gray-400">
                    {{ documents.length }} documento(s) anexado(s)
                </div>
                <div class="flex gap-3">
                    <button 
                        @click="$emit('close')"
                        class="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-white rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500 transition"
                    >
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch, computed, onUnmounted } from 'vue'
import { 
    XIcon, 
    FileTextIcon, 
    TrashIcon, 
    EyeIcon, 
    MaximizeIcon,
    MinimizeIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    PlusIcon,
    MinusIcon,
    RefreshCwIcon,
    ArchiveIcon
} from 'lucide-vue-next'
import apiClient from '@/services/authService'
import { useUserStore } from '@/stores/userStore'

// eslint-disable-next-line no-undef
const props = defineProps({
    appointment: {
        type: Object,
        required: true
    },
    scheduleId: {
        type: Number,
        required: true
    }
})

// eslint-disable-next-line no-undef
const emit = defineEmits(['close', 'document-deleted'])

// Permissões
const userStore = useUserStore()
const canDeleteDocument = computed(() => (userStore.permissions || []).includes('Schedules.delete_document'))

// State
const documents = ref([])
const selectedDocument = ref(null)
const documentUrl = ref(null)
const isLoading = ref(false)
const isLoadingDocument = ref(false)
const documentError = ref(null)
const thumbnailCache = ref(new Map()) // Cache local para thumbnails
const isFullscreen = ref(false)
const currentZoom = ref(1)
const currentDocumentIndex = ref(-1)
const isDragging = ref(false)
const dragOffset = ref({ x: 0, y: 0 })
const dragStart = ref({ x: 0, y: 0 })
const lastDragOffset = ref({ x: 0, y: 0 })
const isDownloadingZip = ref(false)

// Computed properties
const canNavigatePrevious = computed(() => {
    return currentDocumentIndex.value > 0
})

const canNavigateNext = computed(() => {
    return currentDocumentIndex.value < documents.value.length - 1
})

// Methods
const fetchDocuments = async () => {
    if (!props.appointment?.id || !props.scheduleId) return
    
    isLoading.value = true
    documentError.value = null
    
    try {
        const response = await apiClient.get(
            `/api/v1/schedules/${props.scheduleId}/appointments/${props.appointment.id}/documents/`
        )
        documents.value = response.data || []
        
        // Seleciona o primeiro documento automaticamente se houver
        if (documents.value.length > 0 && !selectedDocument.value) {
            selectDocument(documents.value[0])
        }
    } catch (error) {
        console.error('Erro ao buscar documentos:', error)
        documents.value = []
        documentError.value = 'Erro ao carregar lista de documentos. Tente novamente.'
    } finally {
        isLoading.value = false
    }
}

const selectDocument = async (document) => {
    selectedDocument.value = document
    documentUrl.value = null
    documentError.value = null
    currentZoom.value = 1
    // Reset drag offset when changing document
    dragOffset.value = { x: 0, y: 0 }
    lastDragOffset.value = { x: 0, y: 0 }
    
    // Atualiza o índice do documento atual
    currentDocumentIndex.value = documents.value.findIndex(d => d.document_id === document.document_id)
    
    // Sempre carrega o documento (thumbnail ou gera um)
    await loadDocument(document)
}

const loadDocument = async (document) => {
    isLoadingDocument.value = true
    documentError.value = null
    
    try {
        // Verifica se já tem a thumbnail no cache
        if (thumbnailCache.value.has(document.document_id)) {
            documentUrl.value = thumbnailCache.value.get(document.document_id)
        }
        // Prioriza o thumbnail para visualização (performance)
        else if (document.thumbnail_url) {
            documentUrl.value = document.thumbnail_url
            // Adiciona ao cache
            thumbnailCache.value.set(document.document_id, document.thumbnail_url)
        } else {
            // Se não tem thumbnail, tenta gerar automaticamente apenas uma vez
            try {
                const response = await apiClient.post(
                    `/api/v1/schedules/${props.scheduleId}/appointments/${props.appointment.id}/documents/${document.document_id}/generate-thumbnail/`
                )
                
                if (response.data.success && response.data.thumbnail_url) {
                    documentUrl.value = response.data.thumbnail_url
                    // Adiciona ao cache
                    thumbnailCache.value.set(document.document_id, response.data.thumbnail_url)
                    // Atualiza o documento na lista com a nova thumbnail (cache local)
                    const docIndex = documents.value.findIndex(d => d.document_id === document.document_id)
                    if (docIndex !== -1) {
                        documents.value[docIndex].thumbnail_url = response.data.thumbnail_url
                    }
                } else {
                    // Se falhar na geração, usa DICOM como fallback
                    documentUrl.value = document.dicom_url
                }
            } catch (error) {
                console.error('Erro ao gerar thumbnail:', error)
                // Se falhar na geração, usa DICOM como fallback
                documentUrl.value = document.dicom_url
            }
        }
    } catch (error) {
        console.error('Erro ao carregar documento:', error)
        documentError.value = 'Não foi possível carregar o documento. Verifique sua conexão e tente novamente.'
    } finally {
        isLoadingDocument.value = false
    }
}

const deleteDocument = async (document) => {
    if (!confirm('Tem certeza que deseja excluir este documento? Esta ação não pode ser desfeita.')) {
        return
    }
    
    try {
        await apiClient.delete(
            `/api/v1/schedules/${props.scheduleId}/appointments/${props.appointment.id}/documents/${document.document_id}/`
        )
        
        // Remove o documento da lista
        const index = documents.value.findIndex(d => d.document_id === document.document_id)
        if (index !== -1) {
            documents.value.splice(index, 1)
        }
        
        // Remove do cache
        thumbnailCache.value.delete(document.document_id)
        
        // Se o documento excluído era o selecionado, seleciona o próximo ou anterior
        if (selectedDocument.value?.document_id === document.document_id) {
            if (documents.value.length > 0) {
                // Tenta selecionar o próximo, senão o anterior
                const nextIndex = Math.min(index, documents.value.length - 1)
                selectDocument(documents.value[nextIndex])
            } else {
                selectedDocument.value = null
                documentUrl.value = null
                currentDocumentIndex.value = -1
            }
        }
        
        emit('document-deleted', document)
    } catch (error) {
        console.error('Erro ao excluir documento:', error)
        alert('Erro ao excluir documento. Tente novamente.')
    }
}



const downloadAllDocuments = async () => {
    if (isDownloadingZip.value) return
    isDownloadingZip.value = true
    
    try {
        // Faz o download direto do arquivo ZIP
        const response = await apiClient.get(
            `/api/v1/schedules/${props.scheduleId}/appointments/${props.appointment.id}/documents/download-zip/`,
            { responseType: 'blob' }
        )
        
        // Cria o blob e faz o download
        const blob = new Blob([response.data], { type: 'application/zip' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        
        // Define nome do arquivo baseado no paciente e serviço
        const patientName = props.appointment?.patient_name?.replace(/\s+/g, '_') || 'paciente'
        const serviceName = props.appointment?.service_name?.replace(/\s+/g, '_') || 'servico'
        const accessionNumber = props.appointment?.accession_number || props.appointment?.id
        a.download = `documentos_${patientName}_${serviceName}_${accessionNumber}.zip`
        
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
        
    } catch (error) {
        console.error('Erro ao baixar ZIP:', error)
        alert('Erro ao baixar ZIP dos documentos. Tente novamente mais tarde.')
    } finally {
        isDownloadingZip.value = false
    }
}

const formatFileSize = (bytes) => {
    if (!bytes) return '0 B'
    
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
}

const formatDate = (dateString) => {
    if (!dateString) return ''
    return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}

const toggleFullscreen = () => {
    isFullscreen.value = !isFullscreen.value
    
    // Ajusta o zoom quando entra/sai da tela cheia
    if (isFullscreen.value) {
        currentZoom.value = Math.min(currentZoom.value * 1.2, 3)
    } else {
        currentZoom.value = Math.max(currentZoom.value * 0.8, 0.5)
    }
}

const zoomIn = () => {
    currentZoom.value = Math.min(currentZoom.value + 0.2, 3)
    // Reset drag offset when zooming
    dragOffset.value = { x: 0, y: 0 }
    lastDragOffset.value = { x: 0, y: 0 }
}

const zoomOut = () => {
    currentZoom.value = Math.max(currentZoom.value - 0.2, 0.3)
    // Reset drag offset when zooming
    dragOffset.value = { x: 0, y: 0 }
    lastDragOffset.value = { x: 0, y: 0 }
}

const resetZoom = () => {
    currentZoom.value = 1
    // Reset drag offset when resetting zoom
    dragOffset.value = { x: 0, y: 0 }
    lastDragOffset.value = { x: 0, y: 0 }
}

const onImageLoad = (event) => {
    // Reset zoom e drag quando uma nova imagem é carregada
    currentZoom.value = 1
    dragOffset.value = { x: 0, y: 0 }
    lastDragOffset.value = { x: 0, y: 0 }
    
    // Garante que a imagem respeite os limites do container
    const img = event.target
    const container = img.closest('.drag-container')
    
    if (container) {
        const containerRect = container.getBoundingClientRect()
        const imgRect = img.getBoundingClientRect()
        
        // Se a imagem for maior que o container, ajusta o zoom inicial
        if (imgRect.width > containerRect.width || imgRect.height > containerRect.height) {
            const scaleX = (containerRect.width - 32) / imgRect.width // 32px de padding
            const scaleY = (containerRect.height - 32) / imgRect.height
            const scale = Math.min(scaleX, scaleY, 1) // Não aumenta, só diminui se necessário
            
            if (scale < 1) {
                currentZoom.value = scale
            }
        }
    }
}

const onImageError = () => {
    documentError.value = 'Erro ao carregar a imagem do documento. O arquivo pode estar corrompido.'
}

const navigateDocument = (direction) => {
    const newIndex = currentDocumentIndex.value + direction
    
    if (newIndex >= 0 && newIndex < documents.value.length) {
        selectDocument(documents.value[newIndex])
    }
}

const startDrag = (event) => {
    if (currentZoom.value <= 1) return // Só permite arrastar se estiver com zoom
    
    event.preventDefault()
    isDragging.value = true
    
    // Handle both mouse and touch events
    const clientX = event.clientX || (event.touches && event.touches[0]?.clientX)
    const clientY = event.clientY || (event.touches && event.touches[0]?.clientY)
    
    dragStart.value = { x: clientX, y: clientY }
    lastDragOffset.value = { ...dragOffset.value }
}

const onDrag = (event) => {
    if (!isDragging.value || currentZoom.value <= 1) return
    
    event.preventDefault()
    
    // Handle both mouse and touch events
    const clientX = event.clientX || (event.touches && event.touches[0]?.clientX)
    const clientY = event.clientY || (event.touches && event.touches[0]?.clientY)
    
    if (clientX && clientY) {
        const deltaX = clientX - dragStart.value.x
        const deltaY = clientY - dragStart.value.y
        
        // Calculate new offset
        const newX = lastDragOffset.value.x + deltaX
        const newY = lastDragOffset.value.y + deltaY
        
        // Apply limits to prevent dragging too far
        const maxOffset = 200 * currentZoom.value // Limit based on zoom level
        dragOffset.value = {
            x: Math.max(-maxOffset, Math.min(maxOffset, newX)),
            y: Math.max(-maxOffset, Math.min(maxOffset, newY))
        }
    }
}

const stopDrag = () => {
    isDragging.value = false
}

const startTouch = (event) => {
    if (event.touches.length === 1) {
        // Single touch - start drag
        startDrag(event)
    } else if (event.touches.length === 2) {
        // Two touches - start pinch zoom
        isDragging.value = false
        const touch1 = event.touches[0]
        const touch2 = event.touches[1]
        const initialDistance = Math.sqrt(
            Math.pow(touch2.clientX - touch1.clientX, 2) +
            Math.pow(touch2.clientY - touch1.clientY, 2)
        )
        dragStart.value = { distance: initialDistance }
    }
}

const onTouchMove = (event) => {
    if (event.touches.length === 1) {
        // Single touch - continue drag
        onDrag(event)
    } else if (event.touches.length === 2) {
        // Two touches - pinch zoom
        event.preventDefault()
        const touch1 = event.touches[0]
        const touch2 = event.touches[1]
        const currentDistance = Math.sqrt(
            Math.pow(touch2.clientX - touch1.clientX, 2) +
            Math.pow(touch2.clientY - touch1.clientY, 2)
        )
        
        const initialDistance = dragStart.value.distance
        const scale = currentDistance / initialDistance
        const newZoom = Math.max(Math.min(currentZoom.value * scale, 3), 0.3)
        
        // Reset drag offset when zooming
        dragOffset.value = { x: 0, y: 0 }
        lastDragOffset.value = { x: 0, y: 0 }
        
        currentZoom.value = newZoom
        dragStart.value = { distance: currentDistance }
    }
}

const onWheel = (event) => {
    event.preventDefault()
    
    const delta = event.deltaY
    const zoomFactor = delta > 0 ? 0.9 : 1.1
    const newZoom = Math.max(Math.min(currentZoom.value * zoomFactor, 3), 0.3)
    
    // Reset drag offset when zooming
    dragOffset.value = { x: 0, y: 0 }
    lastDragOffset.value = { x: 0, y: 0 }
    
    currentZoom.value = newZoom
}

// Keyboard navigation
const handleKeydown = (event) => {
    if (!selectedDocument.value) return
    
    switch (event.key) {
        case 'ArrowLeft':
            if (canNavigatePrevious.value) {
                event.preventDefault()
                navigateDocument(-1)
            }
            break
        case 'ArrowRight':
            if (canNavigateNext.value) {
                event.preventDefault()
                navigateDocument(1)
            }
            break
        case 'Escape':
            if (isFullscreen.value) {
                event.preventDefault()
                toggleFullscreen()
            } else {
                emit('close')
            }
            break
        case '+':
        case '=':
            event.preventDefault()
            zoomIn()
            break
        case '-':
            event.preventDefault()
            zoomOut()
            break
        case '0':
            event.preventDefault()
            resetZoom()
            break
    }
}

// Lifecycle
onMounted(() => {
    fetchDocuments()
    // Adiciona listener para navegação por teclado
    document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
    // Remove listener de navegação por teclado
    document.removeEventListener('keydown', handleKeydown)
})

// Watch for appointment changes
watch(() => props.appointment, () => {
    if (props.appointment) {
        // Limpa o cache quando muda o agendamento
        thumbnailCache.value.clear()
        selectedDocument.value = null
        documentUrl.value = null
        currentDocumentIndex.value = -1
        currentZoom.value = 1
        fetchDocuments()
    }
}, { immediate: true })
</script>

<style scoped>
/* Custom scrollbar */
.overflow-y-auto::-webkit-scrollbar {
    width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

/* Dark mode scrollbar */
.dark .overflow-y-auto::-webkit-scrollbar-track {
    background: #374151;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
    background: #6b7280;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
}

/* Zoom transitions */
img {
    transition: transform 0.3s ease-in-out;
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    /* Garante que a imagem nunca ultrapasse os limites */
    max-width: 100% !important;
    max-height: 100% !important;
    width: auto !important;
    height: auto !important;
    object-fit: contain;
}

/* Container constraints */
.drag-container {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    /* Garante que o container tenha limites claros */
    position: relative;
    overflow: hidden;
}

/* Viewer container constraints */
.viewer-container {
    overflow: hidden;
    max-width: 100%;
    max-height: 100%;
}

/* Garante que imagens dentro do container respeitem os limites */
.drag-container img {
    max-width: calc(100% - 2rem) !important;
    max-height: calc(100% - 2rem) !important;
    width: auto !important;
    height: auto !important;
    object-fit: contain;
}

/* Garante que todas as imagens no viewer respeitem os limites */
.viewer-container img {
    max-width: 100% !important;
    max-height: 100% !important;
    width: auto !important;
    height: auto !important;
    object-fit: contain;
}

/* Drag cursor styles */
.cursor-grab {
    cursor: grab;
}

.cursor-grabbing {
    cursor: grabbing;
}

/* Navigation arrows hover effects */
.navigation-arrow {
    backdrop-filter: blur(8px);
    transition: all 0.2s ease-in-out;
}

.navigation-arrow:hover {
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

/* Zoom controls */
.zoom-control {
    backdrop-filter: blur(8px);
    transition: all 0.2s ease-in-out;
}

.zoom-control:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* Loading animation */
@keyframes pulse {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.5;
    }
}

.animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Error state styling */
.error-container {
    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
}

.dark .error-container {
    background: linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%);
}

/* Document list item hover effects */
.document-item {
    transition: all 0.2s ease-in-out;
}

.document-item:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Fullscreen mode adjustments */
.fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 9999;
    border-radius: 0;
    max-width: none;
    max-height: none;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .navigation-arrow {
        padding: 0.5rem;
    }
    
    .navigation-arrow svg {
        width: 1.25rem;
        height: 1.25rem;
    }
    
    .zoom-control {
        padding: 0.5rem;
    }
    
    .zoom-control svg {
        width: 1rem;
        height: 1rem;
    }
}

/* Smooth transitions for all interactive elements */
button, .document-item, img {
    transition: all 0.2s ease-in-out;
}

/* Focus states for accessibility */
button:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
}

/* Custom button styles */
.btn-primary {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    transition: all 0.2s ease-in-out;
}

.btn-primary:hover {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-danger {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    transition: all 0.2s ease-in-out;
}

.btn-danger:hover {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

/* Download ZIP button styles */
button[title="Download de todos os documentos em ZIP"] {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    transition: all 0.2s ease-in-out;
}

button[title="Download de todos os documentos em ZIP"]:hover:not(:disabled) {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

button[title="Download de todos os documentos em ZIP"]:disabled {
    background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
}
</style>